import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { NAV_GROUPS } from "@/constants/modules";
import { X, ChevronDown } from "lucide-react";
import { Badge } from "@/components/shared/Badge";
import { useBadgeCounts } from "@/hooks/useBadgeCounts";
import usePmsNewTaskNotifier from "@/hooks/usePmsNewTaskNotifier";

export const TreeNavigation = ({ mobileOpen, onClose, colors = {}, logoUrl, companyName }) => {
  const cDark = colors.cDark || "#072354";
  const cMed = colors.cMed || "#2877D1";
  const cLight = colors.cLight || "#00BFFF";
  const location = useLocation();
  // Batch #14.138 — Polling 30s des compteurs d'action client→admin
  const { counts } = useBadgeCounts();
  // Batch #14.212 — Notif sonore + push browser à chaque nouvelle tâche PMS
  usePmsNewTaskNotifier(counts);

  const findActiveGroup = () => {
    const matchPath = (path) => {
      if (!path) return false;
      const cleanPath = path.split("?")[0];
      return location.pathname === cleanPath || location.pathname.startsWith(cleanPath + "/");
    };
    for (const group of NAV_GROUPS) {
      if (group.children) {
        for (const child of group.children) {
          if (matchPath(child.path)) return group.id;
          if (child.subChildren) {
            for (const sub of child.subChildren) {
              if (matchPath(sub.path)) return group.id;
            }
          }
        }
      }
    }
    return null;
  };

  const [openGroup, setOpenGroup] = useState(findActiveGroup);

  const toggleGroup = (groupId) => {
    setOpenGroup(prev => prev === groupId ? null : groupId);
  };

  const matchPathExact = (path) => {
    if (!path) return false;
    const cleanPath = path.split("?")[0];
    return location.pathname === cleanPath || location.pathname.startsWith(cleanPath + "/");
  };

  const isChildActive = (group) => {
    if (!group.children) return false;
    return group.children.some(c =>
      matchPathExact(c.path) ||
      (c.subChildren && c.subChildren.some(s => matchPathExact(s.path)))
    );
  };

  const linkClasses = (isActive) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${isActive ? "font-semibold" : "hover:bg-white/5"}`;

  const linkStyle = (isActive) =>
    isActive ? { background: `${cMed}25`, color: cLight } : { color: "rgba(255,255,255,0.7)" };

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} />}

      <div
        className={`fixed inset-y-0 left-0 z-50 w-56 flex flex-col h-full transform transition-transform duration-200 ease-in-out lg:static lg:translate-x-0 lg:w-56 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ background: cDark }}
        data-testid="tree-navigation"
      >
        {/* Header */}
        <div className="p-3.5 flex items-center justify-between" style={{ borderBottom: `1px solid ${cMed}20` }}>
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                className="w-7 h-7 rounded object-contain bg-white p-0.5"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <img src="/dg-logo.png" alt="DG Fiscal Flow" className="w-7 h-7 object-contain" />
            )}
            <span className="text-sm font-bold text-white">{companyName || "DG Fiscal Flow"}</span>
          </div>
          <button onClick={onClose} className="lg:hidden p-1 text-white/40 hover:text-white" data-testid="close-sidebar-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-2">
          {NAV_GROUPS.map((group) => {
            const sidebarBadge = counts?.sidebar?.[group.id] || 0;
            const pmsBreakdown = group.id === "pms" ? counts?.hubs?.pms : null;
            const pmsTooltip = pmsBreakdown && sidebarBadge > 0
              ? `À traiter : ${pmsBreakdown.credit} enquête(s) · ${pmsBreakdown.rl31} RL-31 · ${pmsBreakdown.rent_increase} renouvellement(s) · ${pmsBreakdown.revenues} revenu(s) · ${pmsBreakdown.drops} document(s) · ${pmsBreakdown.t777} T777 · ${pmsBreakdown.work_orders} travaux · Entreprise : ${(pmsBreakdown.ent_revenues || 0) + (pmsBreakdown.ent_expenses || 0) + (pmsBreakdown.ent_mileage || 0)} pièce(s)`
              : group.label;
            if (!group.children) {
              return (
                <NavLink
                  key={group.id}
                  to={group.path}
                  end={group.path === "/admin"}
                  onClick={onClose}
                  className={({ isActive }) => linkClasses(isActive)}
                  style={({ isActive }) => linkStyle(isActive)}
                  data-testid={`nav-${group.id}`}
                  title={pmsTooltip}
                >
                  <group.icon className="w-4.5 h-4.5 flex-shrink-0" style={{ width: 18, height: 18 }} />
                  <span className="flex-1">{group.label}</span>
                  {sidebarBadge > 0 && <Badge count={sidebarBadge} testid={`nav-badge-${group.id}`} />}
                </NavLink>
              );
            }

            const isOpen = openGroup === group.id;
            const hasActive = isChildActive(group);

            return (
              <div key={group.id} className="mt-0.5">
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={`w-full ${linkClasses(false)}`}
                  style={hasActive ? { color: cLight } : { color: "rgba(255,255,255,0.7)" }}
                  data-testid={`nav-group-${group.id}`}
                >
                  <group.icon className="flex-shrink-0" style={{ width: 18, height: 18 }} />
                  <span className="flex-1 text-left">{group.label}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 opacity-40 ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                  <div className="ml-3 pl-3 border-l border-white/10 space-y-0.5 py-1">
                    {group.children.map((child) => {
                      const isActive = matchPathExact(child.path);
                      return (
                        <NavLink
                          key={child.id}
                          to={child.path}
                          onClick={onClose}
                          className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs transition ${isActive ? "font-medium" : "hover:bg-white/5"}`}
                          style={isActive ? { background: `${cMed}25`, color: cLight } : { color: "rgba(255,255,255,0.55)" }}
                          data-testid={`nav-${child.id}`}
                        >
                          <child.icon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </>
  );
};
